"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Telefonfelder richten sich selbst
// ============================================================
// Jedes Feld mit `type="tel"` (`Input`) oder `typ="tel"`
// (`InlineFeld`) schreibt eine Nummer nach DIN 5008, sobald sie
// eingefuegt wird oder das Feld verlassen wird: aus
// „Tel. 0911 / 99 88 2-0“ wird „+49 911 99882-0“. Die Regeln
// stehen in `lib/phone-format.ts`; was sich nicht sicher lesen
// laesst, bleibt, wie es getippt wurde.
//
// Die Regeln bringen die Vorwahldaten aller Laender mit (rund
// 20 KB). Geladen werden sie deshalb erst, wenn ein Telefonfeld
// den Zeiger bekommt — nicht auf jeder Seite, auf der irgendein
// Eingabefeld steht. Kommt das Einfuegen schneller als die Daten,
// fuegt der Browser zuerst ein, und das Feld wird danach gerichtet.
//
// Der Server schreibt dieselbe Nummer noch einmal um
// (`normalizePhoneNumber`): ein Dialog, der mit der Eingabetaste
// abgeschickt wird, verlaesst sein Feld nie.
// ============================================================

import type { formatPhoneNumber } from "@/lib/phone-format";

type Formatter = typeof formatPhoneNumber;

let formatter: Formatter | null = null;
let loading: Promise<Formatter> | null = null;

/** Laedt die Regeln einmal je Seite; jeder weitere Aufruf wartet mit. */
export function loadPhoneFormatter(): Promise<Formatter> {
  loading ??= import("@/lib/phone-format").then((m) => (formatter = m.formatPhoneNumber));
  return loading;
}

/**
 * Die Nummer nach DIN 5008 — sofern die Regeln schon da sind und
 * die Angabe eine Nummer ist. Sonst `null`, und es bleibt beim
 * Getippten.
 */
export function formatPhoneIfReady(value: string, country?: string | null): string | null {
  return formatter ? formatter(value, country) : null;
}

/**
 * Setzt den Wert so, dass React es als Eingabe sieht: ueber den
 * Setter des Prototyps, an der Wertverfolgung von React vorbei,
 * und mit einem `input`-Ereignis hinterher. Ein gesteuertes Feld
 * bekommt so sein `onChange`, ein freies einfach den Wert.
 */
function setFieldValue(input: HTMLInputElement, value: string) {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set?.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

/** Das ganze Feld nach DIN 5008, sofern es eine Nummer traegt. */
async function tidy(input: HTMLInputElement, country?: string | null) {
  const format = formatter ?? (await loadPhoneFormatter());
  const formatted = format(input.value, country);
  if (formatted && formatted !== input.value) setFieldValue(input, formatted);
}

/**
 * Beim Einfuegen: gerechnet wird mit dem Feld, wie es NACH dem
 * Einfuegen aussaehe — wer die Vorwahl schon getippt hat und den
 * Rest einfuegt, bekommt die ganze Nummer gerichtet. Ergibt das
 * keine Nummer, fuegt der Browser ganz normal ein.
 *
 * `insertText` statt eines gesetzten Wertes: so bleibt Cmd+Z
 * moeglich und fuehrt zurueck zu dem, was vorher im Feld stand.
 */
export function handlePhonePaste(
  event: React.ClipboardEvent<HTMLInputElement>,
  country?: string | null,
) {
  if (event.defaultPrevented) return;
  const input = event.currentTarget;
  const pasted = event.clipboardData.getData("text/plain");
  if (!pasted) return;

  if (!formatter) {
    // Die Regeln kommen noch: der Browser fuegt ein, danach wird
    // gerichtet. `setTimeout`, weil das Einfuegen erst nach allen
    // Zuhoerern geschieht — ein Mikrotask kaeme davor.
    setTimeout(() => void tidy(input, country), 0);
    return;
  }

  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? start;
  const next = input.value.slice(0, start) + pasted + input.value.slice(end);
  const formatted = formatter(next, country);
  if (!formatted) return;

  event.preventDefault();
  input.select();
  if (!document.execCommand("insertText", false, formatted)) setFieldValue(input, formatted);
}

/** Beim Verlassen: das Feld nach DIN 5008, falls es eine Nummer ist. */
export function tidyPhoneField(input: HTMLInputElement, country?: string | null) {
  if (!formatter) {
    void tidy(input, country);
    return;
  }
  const formatted = formatter(input.value, country);
  if (formatted && formatted !== input.value) setFieldValue(input, formatted);
}
